import { Modal } from '@components/modal/Modal';
import { ForwardedRef, ReactNode, forwardRef, useState } from 'react';
import { ThemeColorKeys } from '../../base/useTheme';
import { ModalFooter } from '../modal/ModalFooter';
import { Message, Question } from './Confirmation.style';

interface ConfirmationProps {
    $color?: ThemeColorKeys;
    title: ReactNode;
    question: ReactNode;
    message?: ReactNode;
    confirmLabel?: string;
    onConfirm?(): void | Promise<void>;
    cancelLabel?: string;
    onCancel?(): void | Promise<void>;
    loading?: boolean;
    disabled?: boolean;
}

function ConfirmationInner(props: ConfirmationProps, ref: ForwardedRef<HTMLDivElement>) {
    const {
        title,
        question,
        message,
        $color = 'primary',
        confirmLabel = 'Confirm',
        onConfirm,
        cancelLabel = 'Cancel',
        onCancel,
        loading,
        disabled,
    } = props;

    const [internalLoading, setInternalLoading] = useState(false);
    const finalLoading = loading || internalLoading;

    const confirmButtonClickHandler = async () => {
        if (onConfirm) {
            setInternalLoading(true);
            try {
                await onConfirm();
            } catch (error) {
                // Swallow so a rejecting onConfirm (e.g. a failed delete) neither leaves the button
                // spinning forever nor becomes an unhandled promise rejection. Mutation errors are
                // already surfaced by the global handler; log the rest for debugging.
                console.error(error);
            } finally {
                setInternalLoading(false);
            }
        }
    };

    const isDisabled = disabled || finalLoading;
    return (
        <Modal.Content ref={ref}>
            <Modal.Header title={title} disabled={isDisabled} onClose={onCancel} />
            <Modal.Body>
                <Question>{question}</Question>
                {message ? <Message $color={$color}>{message}</Message> : null}
            </Modal.Body>
            <ModalFooter
                cancelLabel={cancelLabel}
                doneLabel={confirmLabel}
                done={confirmButtonClickHandler}
                onClose={onCancel}
                disabled={isDisabled}
                loading={finalLoading}
                doneColor={$color}
            />
        </Modal.Content>
    );
}

export const Confirmation = forwardRef(ConfirmationInner);
